import React, {useState, useEffect} from 'react';

export function ArticleViewer({articleId}) {
    const [article, setArticle] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (articleId) {
            loadArticle();
        }
    }, [articleId]);

    const loadArticle = () => {
        try {
            const storedData = localStorage.getItem(`koenig-shared-${articleId}`);
            if (storedData) {
                const articleData = JSON.parse(storedData);
                setArticle(articleData);
            } else {
                setError('Article not found. The link may have expired or been deleted.');
            }
        } catch (err) {
            console.error('Error loading article:', err);
            setError('Failed to load article. Please try again.');
        }
    };

    // Inject global styles for the page
    const globalStyles = `
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        html {
            scroll-behavior: smooth;
        }
        body {
            font-family: 'Georgia', 'Times New Roman', serif;
            background-color: #ffffff;
            color: #292929;
            line-height: 1.7;
            -webkit-font-smoothing: antialiased;
        }
        ::selection {
            background-color: #b4d5fe;
        }
    `;

    // Article content styles
    const articleStyles = `
        .article-wrapper {
            max-width: 680px;
            margin: 0 auto;
            padding: 0 24px;
        }

        .article-header {
            padding-top: 80px;
            padding-bottom: 32px;
            border-bottom: 1px solid #e6e6e6;
            margin-bottom: 32px;
        }

        .article-title {
            font-family: 'Georgia', serif;
            font-size: 48px;
            font-weight: 700;
            line-height: 1.15;
            letter-spacing: -0.02em;
            color: #292929;
            margin-bottom: 24px;
        }

        .article-subtitle {
            font-size: 22px;
            color: #757575;
            font-weight: 400;
            line-height: 1.4;
            margin-bottom: 32px;
        }

        .author-section {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        .author-avatar {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            background: linear-gradient(135deg, #667EEA 0%, #764BA2 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-family: -apple-system, BlinkMacSystemFont, sans-serif;
            font-weight: 600;
            font-size: 20px;
        }

        .author-info {
            display: flex;
            flex-direction: column;
        }

        .author-name {
            font-family: -apple-system, BlinkMacSystemFont, sans-serif;
            font-size: 15px;
            font-weight: 500;
            color: #292929;
        }

        .author-name:hover {
            text-decoration: underline;
            cursor: pointer;
        }

        .article-meta {
            font-family: -apple-system, BlinkMacSystemFont, sans-serif;
            font-size: 14px;
            color: #757575;
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .article-meta span {
            display: flex;
            align-items: center;
        }

        .read-time::before {
            content: '·';
            margin-right: 8px;
        }

        /* Article Content Styles */
        .article-content {
            font-size: 20px;
            line-height: 1.8;
            color: #292929;
            padding-bottom: 80px;
        }

        .article-content > *:first-child {
            margin-top: 0;
        }

        .article-content p {
            margin: 0 0 28px 0;
        }

        .article-content h1 {
            font-family: 'Georgia', serif;
            font-size: 36px;
            font-weight: 700;
            line-height: 1.25;
            margin: 56px 0 20px 0;
            letter-spacing: -0.01em;
        }

        .article-content h2 {
            font-family: 'Georgia', serif;
            font-size: 28px;
            font-weight: 700;
            line-height: 1.3;
            margin: 48px 0 16px 0;
            letter-spacing: -0.01em;
        }

        .article-content h3 {
            font-family: 'Georgia', serif;
            font-size: 22px;
            font-weight: 700;
            line-height: 1.4;
            margin: 40px 0 12px 0;
        }

        .article-content a {
            color: inherit;
            text-decoration: underline;
            text-decoration-color: rgba(41, 41, 41, 0.4);
            text-underline-offset: 2px;
        }

        .article-content a:hover {
            text-decoration-color: #292929;
        }

        .article-content strong {
            font-weight: 700;
        }

        .article-content em {
            font-style: italic;
        }

        .article-content img {
            max-width: 100%;
            height: auto;
            margin: 40px 0;
            display: block;
        }

        .article-content figure {
            margin: 40px 0;
        }

        .article-content figure img {
            margin: 0;
        }

        .article-content figcaption {
            font-family: -apple-system, BlinkMacSystemFont, sans-serif;
            font-size: 14px;
            color: #757575;
            text-align: center;
            margin-top: 12px;
            line-height: 1.5;
        }

        .article-content blockquote {
            font-style: italic;
            font-size: 24px;
            line-height: 1.6;
            color: #292929;
            border-left: 3px solid #292929;
            padding-left: 24px;
            margin: 40px 0;
        }

        .article-content blockquote p {
            margin: 0;
        }

        .article-content code {
            font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Code', monospace;
            font-size: 0.9em;
            background-color: #f5f5f5;
            padding: 3px 6px;
            border-radius: 4px;
        }

        .article-content pre {
            background-color: #292929;
            color: #f5f5f5;
            padding: 24px;
            border-radius: 8px;
            overflow-x: auto;
            margin: 40px 0;
            font-family: 'SF Mono', 'Monaco', 'Inconsolata', monospace;
            font-size: 15px;
            line-height: 1.6;
        }

        .article-content pre code {
            background: none;
            padding: 0;
            color: inherit;
            font-size: inherit;
        }

        .article-content ul, .article-content ol {
            margin: 28px 0;
            padding-left: 32px;
        }

        .article-content li {
            margin: 12px 0;
            padding-left: 8px;
        }

        .article-content hr {
            border: none;
            text-align: center;
            margin: 56px 0;
        }

        .article-content hr::before {
            content: '...';
            font-size: 28px;
            letter-spacing: 16px;
            color: #757575;
        }

        /* Koenig Card Styles */
        .article-content .kg-card {
            margin: 40px 0;
        }

        .article-content .kg-image-card {
            margin: 40px -24px;
        }

        .article-content .kg-image-card img {
            width: 100%;
            margin: 0;
        }

        .article-content .kg-width-wide {
            margin-left: -120px;
            margin-right: -120px;
            max-width: calc(100% + 240px);
        }

        .article-content .kg-width-full {
            margin-left: calc(-50vw + 50%);
            margin-right: calc(-50vw + 50%);
            max-width: 100vw;
        }

        .article-content .kg-bookmark-card {
            border: 1px solid #e6e6e6;
            border-radius: 4px;
            overflow: hidden;
            transition: box-shadow 0.2s ease;
        }

        .article-content .kg-bookmark-card:hover {
            box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        }

        .article-content .kg-bookmark-container {
            display: flex;
            text-decoration: none;
            color: inherit;
        }

        .article-content .kg-bookmark-content {
            flex: 1;
            padding: 20px;
            display: flex;
            flex-direction: column;
        }

        .article-content .kg-bookmark-title {
            font-family: -apple-system, BlinkMacSystemFont, sans-serif;
            font-size: 16px;
            font-weight: 600;
            color: #292929;
            margin-bottom: 8px;
            line-height: 1.4;
        }

        .article-content .kg-bookmark-description {
            font-family: -apple-system, BlinkMacSystemFont, sans-serif;
            font-size: 14px;
            color: #757575;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            line-height: 1.5;
            margin-bottom: 12px;
        }

        .article-content .kg-bookmark-metadata {
            display: flex;
            align-items: center;
            gap: 8px;
            font-family: -apple-system, BlinkMacSystemFont, sans-serif;
            font-size: 13px;
            color: #757575;
        }

        .article-content .kg-bookmark-icon {
            width: 16px;
            height: 16px;
        }

        .article-content .kg-bookmark-thumbnail {
            width: 180px;
            flex-shrink: 0;
        }

        .article-content .kg-bookmark-thumbnail img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            margin: 0;
        }

        .article-content .kg-callout-card {
            padding: 24px;
            border-radius: 4px;
            background-color: #f5f5f5;
            display: flex;
            align-items: flex-start;
            gap: 12px;
        }

        .article-content .kg-callout-emoji {
            font-size: 24px;
            line-height: 1;
        }

        .article-content .kg-callout-text {
            flex: 1;
        }

        .article-content .kg-gallery-card {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 8px;
            margin: 40px -24px;
        }

        .article-content .kg-gallery-image img {
            width: 100%;
            height: 300px;
            object-fit: cover;
            margin: 0;
        }

        .article-content .kg-embed-card {
            margin: 40px 0;
        }

        .article-content .kg-embed-card iframe {
            width: 100%;
            border: none;
        }

        .article-content .kg-video-card video,
        .article-content .kg-audio-card audio {
            width: 100%;
        }

        /* Footer */
        .article-footer {
            border-top: 1px solid #e6e6e6;
            padding: 40px 0 80px 0;
            text-align: center;
        }

        .footer-cta {
            margin-bottom: 32px;
        }

        .footer-cta-title {
            font-family: 'Georgia', serif;
            font-size: 24px;
            font-weight: 700;
            margin-bottom: 8px;
        }

        .footer-cta-text {
            font-family: -apple-system, BlinkMacSystemFont, sans-serif;
            font-size: 16px;
            color: #757575;
        }

        .subscribe-button {
            display: inline-block;
            background: #1a8917;
            color: white;
            font-family: -apple-system, BlinkMacSystemFont, sans-serif;
            font-size: 15px;
            font-weight: 500;
            padding: 12px 24px;
            border-radius: 24px;
            border: none;
            cursor: pointer;
            transition: background-color 0.2s;
            margin-top: 16px;
        }

        .subscribe-button:hover {
            background: #156d12;
        }

        .powered-by {
            font-family: -apple-system, BlinkMacSystemFont, sans-serif;
            font-size: 13px;
            color: #757575;
            margin-top: 40px;
        }

        .powered-by a {
            color: #757575;
            text-decoration: underline;
        }

        /* Responsive */
        @media (max-width: 768px) {
            .article-header {
                padding-top: 48px;
            }

            .article-title {
                font-size: 32px;
            }

            .article-subtitle {
                font-size: 18px;
            }

            .article-content {
                font-size: 18px;
            }

            .article-content h1 {
                font-size: 28px;
            }

            .article-content h2 {
                font-size: 24px;
            }

            .article-content h3 {
                font-size: 20px;
            }

            .article-content blockquote {
                font-size: 20px;
                padding-left: 16px;
            }

            .article-content .kg-image-card,
            .article-content .kg-gallery-card {
                margin-left: -24px;
                margin-right: -24px;
            }

            .article-content .kg-width-wide,
            .article-content .kg-width-full {
                margin-left: -24px;
                margin-right: -24px;
                max-width: calc(100% + 48px);
            }

            .article-content .kg-bookmark-container {
                flex-direction: column;
            }

            .article-content .kg-bookmark-thumbnail {
                width: 100%;
                height: 180px;
                order: -1;
            }
        }
    `;

    // Error page
    if (error) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
                backgroundColor: '#fafafa',
                padding: '24px'
            }}>
                <div style={{
                    textAlign: 'center',
                    maxWidth: '400px'
                }}>
                    <div style={{
                        width: '80px',
                        height: '80px',
                        margin: '0 auto 24px',
                        borderRadius: '50%',
                        backgroundColor: '#fff3f3',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#e53935" strokeWidth="1.5">
                            <circle cx="12" cy="12" r="10"/>
                            <path d="M12 8v4M12 16h.01"/>
                        </svg>
                    </div>
                    <h1 style={{
                        fontSize: '24px',
                        fontWeight: 600,
                        marginBottom: '12px',
                        color: '#292929'
                    }}>Article not found</h1>
                    <p style={{
                        fontSize: '16px',
                        color: '#757575',
                        lineHeight: 1.6
                    }}>{error}</p>
                </div>
            </div>
        );
    }

    // Loading state
    if (!article) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'
            }}>
                <div style={{color: '#757575', fontSize: '16px'}}>Loading...</div>
            </div>
        );
    }

    // Calculate read time (rough estimate: 200 words per minute)
    const wordCount = article.html ? article.html.replace(/<[^>]*>/g, '').split(/\s+/).length : 0;
    const readTime = Math.max(1, Math.ceil(wordCount / 200));

    return (
        <>
            <style dangerouslySetInnerHTML={{__html: globalStyles + articleStyles}} />

            <article className="article-wrapper">
                {/* Article Header */}
                <header className="article-header">
                    <h1 className="article-title">{article.title || 'Untitled'}</h1>

                    <div className="author-section">
                        <div className="author-avatar">A</div>
                        <div className="author-info">
                            <span className="author-name">Author</span>
                            <div className="article-meta">
                                <span>
                                    {new Date(article.createdAt).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                    })}
                                </span>
                                <span className="read-time">{readTime} min read</span>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Article Content */}
                <div
                    className="article-content"
                    dangerouslySetInnerHTML={{__html: article.html}}
                />

                {/* Footer */}
                <footer className="article-footer">
                    <div className="footer-cta">
                        <h3 className="footer-cta-title">Thanks for reading!</h3>
                        <p className="footer-cta-text">Subscribe to get future posts delivered to your inbox.</p>
                        <button className="subscribe-button">Subscribe</button>
                    </div>
                    <p className="powered-by">
                        Published with <a href="https://ghost.org" target="_blank" rel="noopener noreferrer">Ghost</a>
                    </p>
                </footer>
            </article>
        </>
    );
}
